<script setup lang="ts">
// Majlis shell: Work, Build, Govern and secondary tools in More.
// The navigation registry also powers the command palette.
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { RouterLink, useRoute, useRouter } from "vue-router";
import JoinBanner from "@/features/welcome/JoinBanner.vue";
import { useAuthStore } from "@/stores/auth";
import { useRunsStore } from "@/stores/runs";
import { useConnectorManagementStore } from "@/stores/connectorManagement";
import { useUiStore } from "@/stores/ui";
import { useFeedback } from "@/composables/useFeedback";
import { useWorkspaceStore } from "@/stores/workspace";
import { useHotkeys } from "@/composables/useHotkeys";
import { useNotifications } from "@/composables/useNotifications";
import { scheduleName, useAlerts } from "@/composables/useAlerts";
import { useTheme } from "@/lib/theme";
import { ago, duration } from "@/lib/format";
import { runTitle } from "@/lib/runState";
import { planPending, runLink, ticketLink } from "@/lib/tickets";
import Icon from "@/components/ui/Icon.vue";
import ScreenHelp from "@/components/ui/ScreenHelp.vue";
import { CollapsibleRoot, CollapsibleTrigger, CollapsibleContent } from "reka-ui";
import { familyOf, familyTabs, isOnPath, listed, navigation, unfoldedFamily, utilities, type NavItem } from "@/lib/navigation";
import FamilyTabs from "@/components/FamilyTabs.vue";
import BrandLockup from "@/components/ui/BrandLockup.vue";
import CommandPalette from "@/components/mc/CommandPalette.vue";
import ToastStack from "@/components/mc/ToastStack.vue";
import LaunchSheet from "@/features/launch/LaunchSheet.vue";

const auth = useAuthStore();
const runs = useRunsStore();
const connectorsToDecide = useConnectorManagementStore();   // address requests an organisation admin decides (2026-11-02)
const ui = useUiStore();
const feedback = useFeedback();
// feedback about Majlis (2026-10-27): the page as it is, the drawer closed so it doesn't cover what's reported
async function openFeedback() {
  drawer.value = false;
  await new Promise((r) => requestAnimationFrame(() => r(null)));
  const template = route.matched[route.matched.length - 1]?.path ?? route.path;
  await feedback.start(String(route.meta.title ?? document.title), template);
}
const workspace = useWorkspaceStore();
const router = useRouter();
const route = useRoute();
useNotifications();   // its watchers notify when a run finishes (the choices are in Settings → Notifications)
const { theme, toggle: toggleTheme } = useTheme();
const alerts = useAlerts();

const now = ref(Date.now());
const clock = setInterval(() => { now.value = Date.now(); }, 1000);
onMounted(() => { runs.start(); alerts.start(); void workspace.load(); connectorsToDecide.start(); });
onBeforeUnmount(() => clearInterval(clock));

const canLaunch = computed(() => workspace.acts);
const initials = computed(() => {
  const local = (auth.email || "?").split("@")[0];
  const parts = local.split(/[._-]/).filter(Boolean);
  return ((parts[0]?.[0] ?? "?") + (parts[1]?.[0] ?? "")).toUpperCase();
});
const displayName = computed(() => {
  const n = workspace.me?.name;
  if (n) return n;
  const local = (auth.email || "").split("@")[0].split(/[._-]/)[0];
  return local ? local[0].toUpperCase() + local.slice(1) : auth.email;
});

const groups = computed(() => navigation.map((g) => ({
  ...g, items: g.items.filter((n) => listed(n, auth.isAdmin, workspace.workspaces.some((w) => w.team_admin))),
})));
const helps = computed(() => utilities.filter((n) => listed(n, auth.isAdmin, workspace.workspaces.some((w) => w.team_admin))));
// A place's pages unfold beneath it (2026-11-04, Amine: "click then expand the sub menu and it closes when you go
// elsewhere"): the place of the page open unfolds; its arrow unfolds another without leaving the page, and going to a page
// folds the others. "" when the person folded them all.
const here = computed(() => familyOf(route.path));
const peek = ref<string | null>(null);
watch(() => route.path, () => { peek.value = null; });
const unfolded = computed(() => unfoldedFamily(route.path, peek.value));
function toggle(family: string | undefined) {
  if (family) peek.value = unfolded.value === family ? "" : family;
}
function unfolds(n: NavItem): boolean {   // a place with more than one page for this person
  return pagesOf(n).length > 1;
}
function pagesOf(n: NavItem) {
  return n.family ? familyTabs(n.family, auth.isAdmin, workspace.workspaces.some((w) => w.team_admin), workspace.current?.key) : [];
}
// Vue Router's active classes ignore the query, so a run's link compares its run id.
function isRunOn(jobId: string): boolean {
  return route.query.run === jobId || route.params.jobId === jobId;
}
function isOn(n: NavItem): boolean {   // lit while its own page, or any page of its family, is open
  return isOnPath(n, route.path);
}

// Live operations: what is running now, by ticket.
const live = computed(() => runs.active.slice(0, 6));
function elapsed(iso: string) { return duration(now.value - Date.parse(iso)); }
// Recent: "3h ago"; older: the date and time, so repeated failures tell apart.
function when(iso: string) {
  const t = Date.parse(iso);
  if (now.value - t < 20 * 3600_000) return ago(iso, now.value);
  return new Date(t).toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

// Needs attention: plans waiting for a person, schedules the guard stopped, failures nobody has opened.
const plans = computed(() => runs.runs.filter(planPending).slice(0, 4));
const failed = computed(() => runs.attention.slice(0, 4));
const attentionCount = computed(() => plans.value.length + alerts.open.value.length + alerts.budgets.value.length
  + alerts.connectors.value.length + alerts.hub.value.length + alerts.maintenance.value.length + runs.attention.length);
const BUDGET_TITLE: Record<string, string> = { budget_reconciliation: "Billing needs review", budget_paused: "Automated work paused" };

// Whether what you see is current: the run log's own polling is the signal.
const liveness = computed<{ tone: "done" | "working" | "failed"; label: string; title: string }>(() => {
  if (!runs.loaded && !runs.error) return { tone: "working", label: "Connecting…", title: "Loading the run log" };
  const age = runs.lastFetch ? now.value - runs.lastFetch : Infinity;
  if (runs.error && age > 60_000) return { tone: "failed", label: "Disconnected", title: `Last update ${ago(runs.lastFetch || undefined, now.value)}: ${runs.error}` };
  if (runs.error) return { tone: "working", label: "Reconnecting…", title: runs.error };
  return { tone: "done", label: "Live", title: `Updated ${ago(runs.lastFetch, now.value)}` };
});

const crumb = computed(() => {
  if (route.name === "ticket") return String(route.params.key ?? "").toUpperCase();
  if (route.name === "connector") return route.params.name === "new"
    ? ({ cloudwatch: "Connect CloudWatch", database: "Connect a database", mcp: "Add remote MCP", agentcore: "Add MCP on AgentCore" } as Record<string, string>)[String(route.query.kind ?? "")] ?? "Add a connection"
    : String(route.params.name ?? "");
  if (route.name === "telemetry-project") return `CloudWatch · ${String(route.params.key ?? "")}`;
  if (route.name === "workspace") return String(route.params.key ?? "").toUpperCase();
  if (route.name === "biographie") return `Biographie · ${String(route.params.key ?? "").toUpperCase()}`;
  return (route.meta.title as string | undefined) ?? "";
});
const section = computed(() => (route.meta.section as string | undefined) ?? (route.name === "ticket" ? "Tickets" : ""));

// The workspace card: who you are in this tenant. It is placed against the
// window, under its button: the sidebar clips what overflows it, and the card
// is wider than the sidebar.
const wsOpen = ref(false);
const wsButton = ref<HTMLButtonElement | null>(null);
const wsPos = ref({ top: 0, left: 0 });
function closeWs() { wsOpen.value = false; }
function toggleWs() {
  const r = wsButton.value?.getBoundingClientRect();
  if (r) wsPos.value = { top: Math.round(r.bottom + 6), left: Math.round(r.left) };
  wsOpen.value = !wsOpen.value;
}
const onWsAway = (e: Event) => {
  const t = e.target as Node | null;
  if (wsOpen.value && t && !wsButton.value?.contains(t) && !document.getElementById("ws-card")?.contains(t)) closeWs();
};
if (typeof window !== "undefined") {
  window.addEventListener("pointerdown", onWsAway, true);
  window.addEventListener("resize", closeWs);
}
onBeforeUnmount(() => {
  window.removeEventListener("pointerdown", onWsAway, true);
  window.removeEventListener("resize", closeWs);
});

// Narrow screens: the sidebar is a drawer. Closed, it's inert (no focus, hidden from
// assistive tech); open, focus moves in and is trapped; closed again, it returns to the menu.
const drawer = ref(false);
const sidebar = ref<HTMLElement | null>(null);
const menuButton = ref<HTMLButtonElement | null>(null);
const mq = typeof window !== "undefined" ? window.matchMedia("(max-width: 900px)") : null;
const narrow = ref(!!mq?.matches);
const onMq = () => { narrow.value = !!mq?.matches; if (!narrow.value) drawer.value = false; };
mq?.addEventListener("change", onMq);
onBeforeUnmount(() => mq?.removeEventListener("change", onMq));
watch(() => route.fullPath, () => { drawer.value = false; wsOpen.value = false; });
watch(drawer, async (open, was) => {
  await nextTick();
  if (open) sidebar.value?.querySelector<HTMLElement>("a, button")?.focus();
  else if (was && narrow.value) menuButton.value?.focus();
});
function trapDrawer(e: KeyboardEvent) {
  if (!drawer.value || e.key !== "Tab" || !sidebar.value) return;
  const els = Array.from(sidebar.value.querySelectorAll<HTMLElement>("a[href], button:not([disabled])")).filter((el) => el.getClientRects().length > 0);
  if (!els.length) return;
  const first = els[0], last = els[els.length - 1];
  if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
  else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
}

useHotkeys({
  "mod+k": () => { ui.paletteOpen = !ui.paletteOpen; },
  n: () => { if (canLaunch.value) ui.openLaunch(); },
  g: () => router.push("/"),
  m: () => router.push("/map"),
  escape: () => { ui.paletteOpen = false; drawer.value = false; wsOpen.value = false; },
});

</script>

<template>
  <div class="shell" :class="{ 'shell--collapsed': ui.railCollapsed, 'shell--drawer': drawer }" @keydown="trapDrawer">
    <a class="skip" href="#main">Skip to content</a>
    <div v-if="drawer" class="drawer-scrim" aria-hidden="true" @click="drawer = false" />

    <aside id="sidebar" ref="sidebar" class="sidebar" aria-label="Main navigation" @scroll.passive="closeWs" :inert="narrow && !drawer" :aria-modal="narrow && drawer ? 'true' : undefined" :role="narrow && drawer ? 'dialog' : undefined">
      <div class="side-head">
        <RouterLink to="/" class="wordmark" aria-label="Majlis, Mission Control">
          <BrandLockup decorative :mark-only="ui.railCollapsed && !narrow" />
        </RouterLink>
        <button v-if="narrow" class="icon-btn" aria-label="Close navigation" @click="drawer = false"><Icon name="close" :size="16" /></button>
        <button class="icon-btn rail-toggle lbl-hide-narrow" :aria-label="ui.railCollapsed ? 'Expand the sidebar' : 'Collapse the sidebar'" :aria-pressed="ui.railCollapsed" @click="ui.railCollapsed = !ui.railCollapsed">
          <Icon :name="ui.railCollapsed ? 'chevron' : 'back'" :size="15" />
        </button>
      </div>

      <div class="ws-wrap">
        <button
          ref="wsButton" class="workspace" :aria-expanded="wsOpen" aria-controls="ws-card"
          :title="workspace.current ? `${workspace.current.name}, ${workspace.tenantName}` : workspace.tenantName || 'Organisation'"
          @click="toggleWs"
        >
          <span class="ws-avatar" aria-hidden="true">{{ (workspace.current?.key ?? workspace.tenantInitial).slice(0, 2) }}</span>
          <span class="lbl ws-text">
            <span class="ws-name">{{ workspace.current?.name ?? (workspace.tenantName || (workspace.state === "error" ? "Organisation" : "…")) }}</span>
            <small>{{ workspace.current ? workspace.tenantName : "Organisation" }}</small>
          </span>
          <Icon class="lbl" name="down" :size="14" />
        </button>
        <div
          v-if="wsOpen" id="ws-card" class="ws-card" role="dialog" aria-label="Organisation and workspace" :style="{ top: `${wsPos.top}px`, left: `${wsPos.left}px` }"
          @keydown.escape.stop="closeWs(); wsButton?.focus()"
        >
          <p class="eyebrow">Organisation</p>
          <p class="ws-card-name">{{ workspace.tenantName || "Unknown organisation" }}</p>
          <p class="muted small">Majlis serves this organisation. Each workspace is a team's home: its people, Jira projects and agents.</p>
          <div class="kv"><span>You</span><span>{{ auth.email }}</span></div>
          <div class="kv"><span>Organisation role</span><span>{{ workspace.me?.role ?? auth.role }}</span></div>
          <p class="eyebrow ws-eyebrow">Work in</p>
          <div v-if="workspace.workspaces.length" class="ws-choices" role="radiogroup" aria-label="Work in">
            <button
              v-for="w in workspace.workspaces" :key="w.key" type="button" role="radio" class="ws-choice"
              :aria-checked="workspace.current?.key === w.key" @click="workspace.choose(w.key)"
            >
              <span class="mono">{{ w.key }}</span><span>{{ w.name }}</span><small v-if="w.team_admin" class="faint">admin</small>
            </button>
          </div>
          <p v-else class="muted small">You aren't in a workspace yet: you can read what is open to the organisation. To start work,
            <RouterLink to="/workspaces/join" @click="closeWs">ask to join your team's workspace</RouterLink>; its admins decide.</p>
          <!-- 2026-10-29 (Amine, on the one-row list): say why only these, and how to work in another -->
          <p v-if="workspace.workspaces.length" class="muted small ws-hint" data-test="ws-hint">
            You work in the {{ workspace.workspaces.length === 1 ? "workspace" : "workspaces" }} you're in.
            <template v-if="auth.isAdmin">You see every one in All workspaces; to work in another, add yourself on its page.</template>
            <template v-else>To work in another, <RouterLink to="/workspaces/join" @click="closeWs">ask to join it</RouterLink>; its admins decide.</template>
          </p>
          <p v-if="workspace.state === 'error'" class="inline-error">{{ workspace.error }}</p>
          <div class="row ws-links">
            <RouterLink v-if="workspace.current" :to="`/workspaces/${workspace.current.key}`" class="btn btn--sm" @click="closeWs">Open {{ workspace.current.name }}</RouterLink>
            <RouterLink to="/workspaces" class="btn btn--sm btn--ghost" @click="closeWs">All workspaces</RouterLink>
            <RouterLink v-if="auth.isAdmin" to="/projects" class="btn btn--sm btn--ghost" @click="closeWs">Jira projects</RouterLink>
          </div>
        </div>
      </div>

      <nav class="nav" aria-label="Primary">
        <template v-for="g in groups" :key="g.label">
          <section v-if="g.items.length" class="nav-group" :aria-label="g.label || 'Administration'">
            <h2 v-if="g.label" class="nav-title lbl">{{ g.label }}</h2>
            <template v-for="n in g.items" :key="n.to">
              <div class="side-item" :class="{ open: unfolds(n) && unfolded === n.family }">
                <div class="side-row">
                  <RouterLink :to="n.to" class="nav-link" :class="{ on: isOn(n) }" :aria-current="isOn(n) && !unfolds(n) ? 'page' : undefined" :title="n.label">
                    <Icon :name="n.icon" :size="18" /><span class="lbl">{{ n.label }}</span>
                    <span v-if="n.to === '/connectors' && connectorsToDecide.toDecide" class="count lbl" data-what="addresses-waiting"
                          :aria-label="`${connectorsToDecide.toDecide} address request${connectorsToDecide.toDecide === 1 ? '' : 's'} waiting for your decision`">
                      {{ connectorsToDecide.toDecide }}</span>
                  </RouterLink>
                  <button v-if="unfolds(n)" type="button" class="unfold lbl" :aria-expanded="unfolded === n.family" :aria-controls="`subnav-${n.family}`"
                          :aria-label="`${unfolded === n.family ? 'Fold' : 'Show'} ${n.label}'s pages`" @click="toggle(n.family)"><Icon name="down" :size="14" /></button>
                </div>
                <ul v-if="unfolds(n) && unfolded === n.family" :id="`subnav-${n.family}`" class="subnav lbl" data-test="subnav" :aria-label="`${n.label}'s pages`">
                  <li v-for="t in pagesOf(n)" :key="t.path">
                    <RouterLink :to="t.path" class="sub-link" :aria-current="here?.family === n.family && here.tab.label === t.label ? 'page' : undefined">{{ t.label }}</RouterLink>
                  </li>
                </ul>
              </div>
            </template>
          </section>
        </template>
      </nav>

      <nav class="nav nav-help" aria-label="Help and feedback">
        <template v-for="n in helps" :key="n.to">
              <div class="side-item" :class="{ open: unfolds(n) && unfolded === n.family }">
                <div class="side-row">
                  <RouterLink :to="n.to" class="nav-link" :class="{ on: isOn(n) }" :aria-current="isOn(n) && !unfolds(n) ? 'page' : undefined" :title="n.label">
                    <Icon :name="n.icon" :size="18" /><span class="lbl">{{ n.label }}</span>
                  </RouterLink>
                  <button v-if="unfolds(n)" type="button" class="unfold lbl" :aria-expanded="unfolded === n.family" :aria-controls="`subnav-${n.family}`"
                          :aria-label="`${unfolded === n.family ? 'Fold' : 'Show'} ${n.label}'s pages`" @click="toggle(n.family)"><Icon name="down" :size="14" /></button>
                </div>
                <ul v-if="unfolds(n) && unfolded === n.family" :id="`subnav-${n.family}`" class="subnav lbl" data-test="subnav" :aria-label="`${n.label}'s pages`">
                  <li v-for="t in pagesOf(n)" :key="t.path">
                    <RouterLink :to="t.path" class="sub-link" :aria-current="here?.family === n.family && here.tab.label === t.label ? 'page' : undefined">{{ t.label }}</RouterLink>
                  </li>
                </ul>
              </div>
        </template>
      </nav>

      <CollapsibleRoot class="activity-group">
        <CollapsibleTrigger class="nav-link more-trigger" title="Activity" aria-label="Activity">
          <Icon name="bolt" :size="18" /><span class="lbl">Activity</span><span v-if="attentionCount" class="count lbl">{{ attentionCount }}</span>
        </CollapsibleTrigger>
        <CollapsibleContent>
      <section class="group" aria-labelledby="live-h">
        <h2 id="live-h" class="nav-title lbl">Live operations <span v-if="runs.active.length" class="mono">{{ runs.active.length }}</span></h2>
        <ul class="runs">
          <li v-for="r in live" :key="r.job_id">
            <RouterLink :to="runLink(r, 'agents')" class="nav-link run" :class="{ on: isRunOn(r.job_id) }" :title="`${runTitle(r)}: running for ${elapsed(r.created_at)}`">
              <span class="dot pulse" aria-hidden="true" />
              <span class="lbl mono run-key">{{ runTitle(r) }}</span>
              <small class="lbl mono faint">{{ elapsed(r.created_at) }}</small>
            </RouterLink>
          </li>
        </ul>
        <p v-if="!runs.active.length" class="empty lbl">No runs executing</p>
      </section>

      <section v-if="attentionCount" class="group" aria-labelledby="attn-h">
        <h2 id="attn-h" class="nav-title lbl">Needs attention <span class="mono attn">{{ attentionCount }}</span></h2>
        <ul class="runs">
          <li v-for="r in plans" :key="`p${r.job_id}`">
            <RouterLink :to="ticketLink(r.jira_ticket ?? '', { run: r.job_id, view: 'overview' })" class="nav-link two" :title="`${runTitle(r)}: a plan waits for your decision`">
              <Icon name="shield" :size="16" class="brand" />
              <span class="lbl stack"><span>Plan to review</span><small class="mono faint">{{ runTitle(r) }}</small></span>
            </RouterLink>
          </li>
          <li v-for="a in alerts.open.value" :key="a.id">
            <RouterLink to="/schedules" class="nav-link two" :title="`${a.schedule_path}: ${a.message}`">
              <Icon name="warning" :size="16" class="bad" />
              <span class="lbl stack"><span>{{ a.disabled ? "Paused schedule" : "Failing schedule" }}</span><small class="mono faint">{{ scheduleName(a.schedule_path) }}</small></span>
            </RouterLink>
          </li>
          <li v-for="a in alerts.budgets.value" :key="a.id">
            <RouterLink to="/budgets" class="nav-link two" :title="a.message">
              <Icon name="dollar" :size="16" class="bad" />
              <span class="lbl stack"><span>{{ BUDGET_TITLE[a.kind] ?? "Budget past its line" }}</span><small class="mono faint">{{ a.kind === "budget_reconciliation" ? "Reported usage and billing" : a.label }}</small></span>
            </RouterLink>
          </li>
          <li v-for="a in alerts.hub.value" :key="a.id">
            <RouterLink to="/agents" class="nav-link two" :title="a.message">
              <Icon name="warning" :size="16" class="bad" />
              <span class="lbl stack"><span>{{ a.title ?? "The hub isn't taking new permissions" }}</span><small class="faint">new agents wait for their tools</small></span>
            </RouterLink>
          </li>
          <li v-for="a in alerts.maintenance.value" :key="a.id" data-test="maintenance-alert">
            <RouterLink to="/" class="nav-link two" :title="a.message">
              <Icon name="warning" :size="16" class="bad" />
              <span class="lbl stack"><span>{{ a.title }}</span><small class="faint">noticed {{ ago(a.raised_at) }}</small></span>
            </RouterLink>
          </li>
          <li v-for="a in alerts.connectors.value" :key="a.id">
            <RouterLink to="/connectors" class="nav-link two" :title="a.message">
              <Icon name="warning" :size="16" class="bad" />
              <span class="lbl stack"><span>Connector changed</span><small class="mono faint">{{ a.connector ?? "review it again" }}</small></span>
            </RouterLink>
          </li>
          <li v-for="r in failed" :key="`f${r.job_id}`">
            <RouterLink :to="runLink(r, 'activity')" class="nav-link run two" :class="{ on: isRunOn(r.job_id) }" :title="`${runTitle(r)} failed ${ago(r.finished_at ?? r.created_at)}`">
              <span class="dot bad" aria-hidden="true" />
              <span class="lbl stack"><span class="mono run-key">{{ runTitle(r) }}</span><small class="faint">failed {{ when(r.finished_at ?? r.created_at) }}{{ r.error_step ? ` · ${r.error_step}` : "" }}</small></span>
            </RouterLink>
          </li>
        </ul>
      </section>

        </CollapsibleContent>
      </CollapsibleRoot>

      <div class="profile">
          <span class="live" :data-tone="liveness.tone" :title="liveness.title" role="status">
            <span class="dot" aria-hidden="true" />{{ liveness.label }}
          </span>

        <div class="me-row">
          <!-- your profile opens your settings: profile, Slack, notifications (2026-10-23) -->
          <RouterLink to="/settings" class="me-link" :class="{ on: route.path.startsWith('/settings') }" :title="`Settings · ${auth.email}`" aria-label="Your settings">
            <span class="avatar" aria-hidden="true">{{ initials }}</span>
            <span class="lbl me">
              <span class="me-name">{{ displayName }}</span>
              <span class="me-role">{{ workspace.me?.role ?? auth.role }}</span>
            </span>
          </RouterLink>
          <button class="icon-btn" aria-label="Sign out" title="Sign out" @click="auth.signOut()">
            <Icon name="logout" :size="16" />
          </button>
        </div>
      </div>
    </aside>

    <div class="frame" :inert="narrow && drawer">
      <JoinBanner />
      <header class="topbar">
        <div class="crumbs">
          <button ref="menuButton" class="icon-btn menu" :aria-label="drawer ? 'Close navigation' : 'Open navigation'" aria-controls="sidebar" :aria-expanded="drawer" @click="drawer = !drawer">
            <Icon name="menu" />
          </button>
          <span class="crumb-ws">{{ workspace.tenantName || "Organisation" }}</span>
          <template v-if="section"><span class="faint" aria-hidden="true">/</span><span :class="{ strong: !crumb || crumb === section }">{{ section }}</span></template>
          <template v-if="crumb && crumb !== section"><span class="faint" aria-hidden="true">/</span><strong :class="{ 'mono-if-key': route.name === 'ticket' }">{{ crumb }}</strong></template>
          <ScreenHelp />
        </div>
        <div class="top-actions">
          <button class="search" aria-label="Search runs, tickets and pages" @click="ui.paletteOpen = true">
            <Icon name="search" :size="17" />
            <span class="search-label">Search anything</span>
            <kbd>⌘ K</kbd>
          </button>
          <button class="feedback-btn" data-test="feedback-open" data-feedback-ui :aria-busy="feedback.capturing.value" :disabled="feedback.capturing.value"
                  title="Report a bug, an idea or a question about this page" @click="openFeedback">
            <Icon name="message" :size="16" /><span class="feedback-label">{{ feedback.capturing.value ? "Preparing…" : "Send feedback" }}</span>
          </button>
          <button class="icon-btn" :aria-label="`Switch to the ${theme === 'dark' ? 'light' : 'dark'} theme`" :title="theme === 'dark' ? 'Light theme' : 'Dark theme'" @click="toggleTheme">
            <Icon :name="theme === 'dark' ? 'sun' : 'moon'" />
          </button>
          <button v-if="canLaunch" class="btn btn--primary new-run" aria-label="New run" @click="ui.openLaunch()">
            <Icon name="plus" /><span class="new-run-label">New run</span>
          </button>
        </div>
      </header>

      <main id="main" class="content" tabindex="-1">
        <FamilyTabs v-if="ui.railCollapsed && !narrow" />   <!-- icons only: no room for a place's pages beneath it -->
        <slot />
      </main>
    </div>

    <CommandPalette />
    <LaunchSheet />
    <ToastStack />
  </div>
</template>

<style scoped>
.shell { display: grid; grid-template-columns: 232px minmax(0, 1fr); min-height: 100vh; }
.shell--collapsed { grid-template-columns: 64px minmax(0, 1fr); }
.skip { position: fixed; top: -60px; left: 16px; z-index: 100; padding: var(--space-3) var(--space-4); border-radius: var(--radius-s); background: var(--brand-solid); color: var(--brand-ink); }
.skip:focus { top: 10px; }

/* ── sidebar ── */
.sidebar {
  position: sticky; top: 0; height: 100vh; display: flex; flex-direction: column; padding: var(--space-6) var(--space-4) var(--space-4);
  border-right: 1px solid var(--line); background: var(--bg-sunk); overflow-y: auto; overflow-x: hidden; z-index: 40;
}
.side-head { display: flex; align-items: center; justify-content: space-between; gap: var(--space-2); padding: 0 var(--space-2) var(--space-5); }
.wordmark { display: flex; min-width: 0; }
.rail-toggle { width: 26px; height: 26px; } /* not "collapse": Tailwind's utility of that name hides it */

.ws-wrap { position: relative; margin-bottom: var(--space-4); }
.workspace {
  display: flex; align-items: center; gap: var(--space-3); width: 100%; padding: var(--space-3) var(--space-3); border: 1px solid var(--line); border-radius: var(--radius-s);
  background: transparent; text-align: left; color: var(--ink);
}
.workspace:hover { background: var(--bg-hover); }
.ws-avatar { display: grid; place-items: center; width: 26px; height: 26px; border-radius: var(--radius-s); flex: none; border: 1px solid var(--line); background: var(--bg-hover); font: var(--text-xs) var(--font-mono); }
.ws-text { display: flex; flex-direction: column; min-width: 0; flex: 1; line-height: 1.25; }
.ws-name { font-size: var(--text-sm); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ws-text small { font-size: var(--text-xs); color: var(--muted); }
.ws-card {
  position: fixed; z-index: 60; width: 320px; max-width: calc(100vw - 24px); padding: var(--space-4) var(--space-5);
  background: var(--bg-elev); border: 1px solid var(--line); border-radius: var(--radius-m); box-shadow: var(--shadow-3);
}
.ws-card-name { font-size: var(--text-md); font-weight: 600; margin: var(--space-2) 0 var(--space-2); }
.ws-card .inline-error { margin-top: var(--space-3); }
.ws-links { margin-top: var(--space-3); flex-wrap: wrap; gap: var(--space-2); }
.ws-eyebrow { margin-top: var(--space-4); }
.ws-choices { display: flex; flex-direction: column; gap: var(--space-1); margin-top: var(--space-2); }
.ws-hint { margin: var(--space-2) 0 0; }
.ws-choice { display: flex; gap: var(--space-3); align-items: baseline; text-align: left; padding: var(--space-2) var(--space-3); border-radius: var(--radius-s); border: 1px solid transparent; background: none; color: var(--ink); cursor: pointer; font: inherit; font-size: var(--text-sm); }
.ws-choice:hover { background: var(--bg-hover); }
.ws-choice[aria-checked="true"] { background: var(--brand-soft); border-color: color-mix(in srgb, var(--brand) 30%, transparent); }
.ws-choice .mono { font-size: var(--text-xs); color: var(--muted); min-width: 4ch; }
.ws-choice small { margin-left: auto; }

.nav { display: flex; flex-direction: column; }
.nav-title { display: flex; align-items: center; gap: var(--space-3); font: var(--text-xs) var(--font-ui); letter-spacing: 0; text-transform: none; color: var(--muted); margin: var(--space-5) var(--space-3) var(--space-3); font-weight: 400; }
.nav-title .attn { color: var(--brand); }
.nav-link {
  display: flex; align-items: center; gap: var(--space-4); min-height: 36px; padding: var(--space-3) var(--space-3); margin-bottom: var(--space-1); border: 1px solid transparent; border-radius: var(--radius-s);
  font-size: var(--text-sm); color: var(--muted); min-width: 0;
}
.nav-link:hover { background: var(--bg-hover); color: var(--ink); }
.nav-link.on { background: var(--brand-soft); color: var(--brand); border-color: color-mix(in srgb, var(--brand) 18%, transparent); }
.nav-link .lbl { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.count { margin-left: auto; background: var(--bg-elev); border-radius: var(--radius-s); font-size: var(--text-xs); padding: var(--space-1) var(--space-2); color: var(--ink); }
.runs { list-style: none; margin: 0; padding: 0; }
.run { gap: var(--space-3); }
.run-key { font-size: var(--text-xs); color: var(--ink); flex: 1; }
.nav-link small { margin-left: auto; font-size: var(--text-xs); flex: none; }
.trunc { max-width: 80px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.dot.pulse { color: var(--working); animation: pulse-ring 1.8s var(--ease) infinite; margin: 0 var(--space-2); }
.dot.bad, .bad { color: var(--failed); }
.dot.bad { margin: 0 var(--space-2); }
.empty { margin: 0 var(--space-3); font-size: var(--text-xs); color: var(--faint); }
.tools { margin-top: var(--space-2); }

.profile { margin-top: auto; padding: var(--space-4) var(--space-2) 0; border-top: 1px solid var(--line); }
.me-row { display: flex; align-items: center; gap: var(--space-2); }
.me-link { display: flex; align-items: center; gap: var(--space-3); flex: 1; min-width: 0; color: inherit; text-decoration: none;
  padding: var(--space-1) var(--space-2); margin-left: calc(-1 * var(--space-2)); border-radius: var(--radius-s); }
.me-link:hover, .me-link.on { background: var(--bg-hover); }
.me-link:focus-visible { outline: 2px solid var(--brand); outline-offset: 1px; }
.avatar { display: grid; place-items: center; width: 30px; height: 30px; border-radius: var(--radius-s); flex: none; border: 1px solid var(--line); background: var(--brand-soft); color: var(--brand); font: var(--text-xs) var(--font-mono); }
.me { display: flex; flex-direction: column; min-width: 0; flex: 1; line-height: 1.25; }
.me-name { font-size: var(--text-sm); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.me-role { font-size: var(--text-xs); color: var(--muted); text-transform: capitalize; }
.me-row .icon-btn { width: 28px; height: 28px; }
.nav-link.two { align-items: flex-start; }
.nav-link.two .icon { margin-top: var(--space-1); }
.stack { display: flex; flex-direction: column; line-height: 1.3; }
.stack small { margin: 0; font-size: var(--text-xs); overflow: hidden; text-overflow: ellipsis; }
.brand { color: var(--brand); }

/* collapsed rail: icons only */
.shell--collapsed .lbl { display: none; }
.shell--collapsed .side-head { flex-direction: column; padding-bottom: var(--space-4); }
.shell--collapsed .nav-link { justify-content: center; padding: var(--space-3) 0; }
.shell--collapsed .workspace { justify-content: center; padding: var(--space-3) 0; }
.shell--collapsed .nav-title { height: 1px; margin: var(--space-4) var(--space-3); background: var(--line); overflow: hidden; color: transparent; }
.shell--collapsed .me-row { flex-direction: column; }

/* ── frame ── */
.frame { min-width: 0; display: flex; flex-direction: column; }
.topbar {
  position: sticky; top: 0; z-index: 30; height: 56px; display: flex; align-items: center; justify-content: space-between; gap: var(--space-4);
  padding: 0 var(--space-7); border-bottom: 1px solid var(--line); background: color-mix(in srgb, var(--bg) 88%, transparent); backdrop-filter: blur(8px);
}
.crumbs { display: flex; align-items: center; gap: var(--space-4); font-size: var(--text-xs); color: var(--muted); min-width: 0; }
.crumbs strong, .crumbs .strong { color: var(--ink); font-weight: 450; }
.crumbs > * { white-space: nowrap; }
.crumbs > strong, .crumbs > .strong { overflow: hidden; text-overflow: ellipsis; min-width: 0; }   /* the page's name gives way; its help button doesn't */
.mono-if-key { font-family: var(--font-mono); font-size: var(--text-xs); }
.menu { display: none; }
.top-actions { display: flex; align-items: center; gap: var(--space-5); flex: none; }
.live { display: inline-flex; align-items: center; gap: var(--space-3); font: var(--text-xs) var(--font-mono); color: var(--muted); white-space: nowrap; }
.live[data-tone="done"] .dot { color: var(--done); }
.live[data-tone="working"] .dot { color: var(--working); }
.live[data-tone="failed"] { color: var(--failed); }
.live[data-tone="failed"] .dot { color: var(--failed); }
.search { display: flex; align-items: center; gap: var(--space-3); padding: var(--space-2) var(--space-3); border: 0; border-radius: var(--radius-s); background: none; color: var(--muted); font-size: var(--text-xs); }
.search:hover { color: var(--ink); background: var(--bg-hover); }
.feedback-btn { display: inline-flex; align-items: center; gap: var(--space-2); padding: var(--space-2) var(--space-3); border: 1px solid var(--line-2);
  border-radius: var(--radius-s); background: none; color: var(--muted); font-size: var(--text-xs); cursor: pointer; }
.feedback-btn:hover:not(:disabled) { color: var(--ink); background: var(--bg-hover); }
.feedback-btn:disabled { cursor: progress; opacity: .8; }
.content { flex: 1; min-width: 0; outline: none; }

/* ── narrow: the sidebar becomes a drawer ── */
@media (max-width: 900px) {
  .shell, .shell--collapsed { grid-template-columns: minmax(0, 1fr); }
  .sidebar { position: fixed; left: 0; top: 0; bottom: 0; width: 264px; transform: translateX(-102%); transition: transform var(--speed-med) var(--ease-out); box-shadow: var(--shadow-3); }
  .shell--drawer .sidebar { transform: none; }
  .shell--collapsed .lbl { display: revert; }
  .shell--collapsed .side-head { flex-direction: row; padding-bottom: var(--space-5); } /* collapsed on a desktop, then narrowed: the drawer's head stays a row */
  .lbl-hide-narrow { display: none; }
  .drawer-scrim { position: fixed; inset: 0; z-index: 35; background: #0007; }
  .menu { display: inline-grid; margin-left: calc(-1 * var(--space-2)); }
  .topbar { padding: 0 var(--space-5); gap: var(--space-3); }
  .live { display: none; }
  .search-label, .search kbd, .feedback-label { display: none; }
  .top-actions { gap: var(--space-2); }
}
@media print {
  .shell, .shell--collapsed { display: block; }
  .sidebar, .topbar, .skip, .drawer-scrim { display: none !important; }
}
@media (max-width: 520px) {
  .crumb-ws, .crumb-ws + .faint { display: none; }
  .new-run { padding: 0 var(--space-3); }
  .new-run-label { display: none; }
}
.sidebar { padding: var(--space-5) var(--space-4); }
.side-head { padding-bottom: var(--space-5); }
.nav-group { margin-bottom: var(--space-3); }
.side-row { display: flex; align-items: center; }
.side-row .nav-link { flex: 1; min-width: 0; }
.unfold { display: grid; place-items: center; width: 28px; height: 28px; margin-left: calc(-1 * var(--space-2)); border: 0;
  background: transparent; color: var(--muted); border-radius: var(--radius-s); cursor: pointer; flex: none; }
.unfold:hover { color: var(--ink); background: var(--bg-hover); }
.unfold svg { transition: transform .15s ease; }
.side-item.open .unfold svg { transform: rotate(180deg); }
.subnav { list-style: none; margin: var(--space-1) 0 var(--space-2) calc(var(--space-4) + 8px); padding: 0 0 0 var(--space-3);
  display: flex; flex-direction: column; gap: 1px; border-left: 1px solid var(--line-2); }
.sub-link { display: block; padding: var(--space-2) var(--space-3); border-radius: var(--radius-s); font-size: var(--text-sm);
  color: var(--muted); text-decoration: none; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.sub-link:hover { color: var(--ink); background: var(--bg-hover); }
.sub-link[aria-current="page"] { color: var(--ink); background: var(--brand-soft); font-weight: 550; }
@media (prefers-reduced-motion: reduce) { .unfold svg { transition: none; } }
.nav-help { margin: var(--space-2) 0 var(--space-1); padding-top: var(--space-3); border-top: 1px solid var(--line-2); }   /* Guide and Feedback, pinned */
.nav-title { margin: var(--space-3) var(--space-4); font-family: var(--font-ui); font-size: var(--text-xs); font-weight: 600; letter-spacing: 0; text-transform: none; }
.nav-link { min-height: 36px; border: 0; font-size: var(--text-sm); gap: var(--space-3); }
.nav-link.on { font-weight: 550; }
.more-trigger { width: 100%; background: transparent; text-align: left; }
.disclosure { margin-left: auto; transition: transform var(--speed-quick); }
.more-trigger[data-state="open"] .disclosure { transform: rotate(180deg); }
.activity-group { margin-bottom: var(--space-5); }
.profile .live { display: flex; margin-bottom: var(--space-4); font-family: var(--font-ui); }
.ws-text small { display: none; }
.workspace { border-color: transparent; background: var(--bg-hover); }
.top-actions { gap: var(--space-3); }
.search { border: 1px solid var(--line-2); }
@media (max-width: 900px) {
  .shell--collapsed .nav-title { height: auto; background: none; color: var(--muted); }   /* faint on the sunk sidebar: 4.37:1 */
  .shell--collapsed .nav-link { justify-content: flex-start; padding: var(--space-3) var(--space-4); }
  .shell--collapsed .workspace, .shell--collapsed .me-row { flex-direction: row; justify-content: flex-start; }
}
</style>
